
import { SidebarDrawer } from '@/components/custom/DrawerRight'
import { useWorkspace } from '@/features/workspace/stores/workspace.store'
import AI_Prompt from '@/shared/components/ui/ai-prompt'
import { Button } from '@/shared/components/ui/button'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import TypewriterTitle from '@/shared/components/ui/typing-text'
import { cn } from '@/shared/lib/utils'
import { getStory } from '@/service/local-db'
import { AnimatePresence, motion } from 'framer-motion'
import { Check, ChevronDown, Globe } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import {
    AIChatMode,
    aIChatModeMetaData,
    useSpAIWriterChatStore,
} from '../../../stores/sp-ai-writer-chat.store'
import { useSpEditorProvider } from '../SpEditorProvider'
import { BaseNode } from '../nodes/BaseNode'
import { NodeType } from '../nodes/NodeCore'
import { SerializedContent, serializedContentToString } from '../utils/JsonUtils'
import { getCurrentNode, insertNodesAsNextScene, replaceCurrentScene } from '../utils/NodeUtils'
import { AppError } from '@/shared/types/AppError'
import { useSceneStore } from '@/features/shared/stores/scene.store'
import { useGenerateSceneMutation, useModifySceneMutation } from '@/features/screenplay/queries/screenplay.ai.queries'

export const NodeTypeClassMap: Record<NodeType, string> = {
    [NodeType.Scene]: 'uppercase font-bold',
    [NodeType.Action]: '',
    [NodeType.Character]: 'uppercase text-center',
    [NodeType.Parans]: 'italic',
    [NodeType.Dialogue]: 'text-center',
    [NodeType.Transition]: 'text-right uppercase',
    [NodeType.Shot]: '',
    [NodeType.PageBreak]: 'mb-4',
}

// using in StAIWriterChatPlugin.tsx
export const renderNodesAsParagraphs = (nodeInput: SerializedContent[]) => {
    return (
        <div
            className="font-script bg-slate-100 p-4 text-[14px
         rounded-xl"
        >
            {nodeInput.map((node) => {
                const cssClass = NodeTypeClassMap[node.type as NodeType]

                return node.children.map((child, index) => (
                    <p key={`${node.id}-${index}`} className={`${cssClass} py-1`}>
                        {child.text}
                    </p>
                ))
            })}
        </div>
    )
}

const SpAIWriterChatPlugin = () => {
    const { isDrawerOpen, mode, setIsDrawerOpen, inputNodes, outputNodes, setOutputNodes } =
        useSpAIWriterChatStore()
    const { editor } = useSceneStore()
    const { project } = useWorkspace()
    const fileName = 'master'
    const storyContent = useRef<string>(undefined)
    const [selectedLanguage, setSelectedLanguage] = useState('English')
    const LANGUAGES = ['English', 'Malayalam', 'Tamil']

    const generateSceneMutation = useGenerateSceneMutation({
        onSuccess: (data: SerializedContent[]) => {
            console.log('AI output:', data)
            setOutputNodes(data)
        },
        onError: (error: AppError) => {
            console.error('Failed to generate AI output:', error)
        }
    })

    // Added a useEffect to properly log outputNodes whenever it changes
    useEffect(() => {
        if (outputNodes.length > 0) {
            console.log('outputNodes successfully updated in state:', outputNodes)
        }
    }, [outputNodes])

    const modifySceneMutation = useModifySceneMutation({
        onSuccess: (data: SerializedContent[]) => {
            setOutputNodes(data)
        },
        onError: (error: AppError) => {
            console.error('Failed to modify AI output:', error)
        }
    })

    const isLoading = generateSceneMutation.isPending || modifySceneMutation.isPending;


    useEffect(() => {
        if (!project?.id) return
        async function fetchStory() {
            if (!project) return
            const story = await getStory(project.id, fileName)
            if (story) {
                storyContent.current = serializedContentToString(story.content)
            }
        }

        fetchStory().catch((error) => {
            console.error('Error fetching story:', error)
        })
    }, [project])

    const handlePromptSubmit = async (text: string) => {
        const payload = {
            sceneData: inputNodes,
            userPrompt: text,
            story: storyContent.current,
            language: selectedLanguage,
        };

        if (mode === AIChatMode.create_scene) {
            generateSceneMutation.mutate(payload)
        } else {
            modifySceneMutation.mutate(payload)
        }
    }
    if (!editor) return null
    return (
        <SidebarDrawer
            isOpen={isDrawerOpen}
            onClose={() => setIsDrawerOpen(false)}
            title={aIChatModeMetaData[mode].title}
        >
            <div className="flex flex-col h-full p-4 ">
                <div className="flex-1 overflow-auto space-y-4 mb-4">
                    {inputNodes && renderNodesAsParagraphs(inputNodes)}

                    {outputNodes.length > 0 && (
                        <div className="border-t pt-4">
                            <p className="text-sm font-semibold text-muted-foreground mb-2">
                                AI Suggestion
                            </p>
                            {renderNodesAsParagraphs(outputNodes)}
                        </div>
                    )}
                </div>

                <AI_Prompt
                    onSubmit={handlePromptSubmit}
                    customButton={buildAIActionButton()}
                    placeholder={
                        isLoading
                            ? 'Wait.. the scene building on process'
                            : aIChatModeMetaData[mode].promptPlaceholder
                    }
                />
            </div>
        </SidebarDrawer>
    )

    function buildAIActionButton() {
        if (isLoading) {
            return (
                <div className="flex-1 h-full">
                    <TypewriterTitle
                        sequences={[
                            {
                                text: 'Your story is on the assembly line...',
                                deleteAfter: true,
                            },
                            {
                                text: 'Loading your next masterpiece...',
                                deleteAfter: true,
                            },
                            { text: 'This story’s about to live.', deleteAfter: true },
                        ]}
                    />
                </div>
            )
        }

        if (outputNodes.length < 1) return buildLanguageDropdown()
        if (mode === AIChatMode.create_scene) return buildInsertButton()
        if (mode === AIChatMode.edit_scene) return buildReplaceButton()
    }

    function buildLanguageDropdown() {
        return (
            <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                    <Button
                        variant="ghost"
                        className="flex items-center gap-1 h-8 pl-1 pr-2 text-xs rounded-md dark:text-white hover:bg-black/10 dark:hover:bg-white/10 focus-visible:ring-1 focus-visible:ring-offset-0 focus-visible:ring-blue-500"
                    >
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={selectedLanguage}
                                initial={{ opacity: 0, y: -5 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 5 }}
                                transition={{ duration: 0.15 }}
                                className="flex items-center gap-1"
                            >
                                <Globe className="w-4 h-4 opacity-50" />
                                {selectedLanguage}
                                <ChevronDown className="w-3 h-3 opacity-50" />
                            </motion.div>
                        </AnimatePresence>
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                    className={cn(
                        'min-w-[10rem]',
                        'border-black/10 dark:border-white/10',
                        'bg-gradient-to-b from-white via-white to-neutral-100 dark:from-neutral-950 dark:via-neutral-900 dark:to-neutral-800'
                    )}
                >
                    {LANGUAGES.map((lang) => (
                        <DropdownMenuItem
                            key={lang}
                            onSelect={() => setSelectedLanguage(lang)}
                            className="flex items-center justify-between gap-2"
                        >
                            <div className="flex items-center gap-2">
                                <Globe className="w-4 h-4 opacity-50" />
                                <span>{lang}</span>
                            </div>
                            {selectedLanguage === lang && (
                                <Check className="w-4 h-4 text-blue-500" />
                            )}
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>
        )
    }
    function buildReplaceButton() {
        return (
            <Button
                className="flex-1"
                onClick={() => {
                    editor?.update(() => {
                        replaceCurrentScene(outputNodes, (getCurrentNode() as BaseNode)!)
                    })
                    setIsDrawerOpen(false)
                }}
            >
                Replace Current Scene
            </Button>
        )
    }
    function buildInsertButton() {
        return (
            <Button
                className="flex-1"
                onClick={() => {
                    console.log('editor', editor)
                    editor?.update(() => {
                        console.log('outputNodes', outputNodes)
                        insertNodesAsNextScene(outputNodes, (getCurrentNode() as BaseNode)!)
                    })
                    setIsDrawerOpen(false)
                }}
            >
                {' '}
                Insert to Screenplay
            </Button>
        )
    }
}

export default SpAIWriterChatPlugin
